import React from "react";
import { Card, Stack, TextInput, Group, Button, Alert, Code, Select, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { usePostMultiplacerProjectEditProjectMutation } from "../../openapi";
import { useAppSelector } from "../app/hooks";
import { selectHttpCurrentProjct } from "../projectSelectors";
import { IFileAttrField } from "@multiconsult-group/multiplacer-core";

type FormValues = {
  projectNo: number | string;
  title: string;
  compCodePath: string;
  pnsPath: string;
  areaPath: string;
  cadCompCodePath: string;
  mmiPath: string;
  disciplineFileAttr: IFileAttrField;
};

export const ProjectBasicsCard: React.FC = () => {
  const project = useAppSelector(selectHttpCurrentProjct);

  const [editProject, { isLoading, isError, error, isSuccess }] =
    usePostMultiplacerProjectEditProjectMutation();

  const form = useForm<FormValues>({
    initialValues: {
      projectNo: project?.projectNo ?? "",
      title: project?.title ?? "",
      compCodePath: project?.compCodePath ?? "",
      pnsPath: project?.pnsPath ?? "",
      areaPath: project?.areaPath ?? "",
      cadCompCodePath: (project as any)?.cadCompCodePath ?? "",
      mmiPath: (project as any)?.mmiPath ?? "",
      disciplineFileAttr: (project?.disciplineFileAttr ?? "fileAttr1") as IFileAttrField,
    },
    validate: {
      title: (v) => (v?.trim().length >= 3 ? null : "Minst 3 tegn"),
      compCodePath: (v) => (v?.trim() ? null : "Påkrevd"),
      pnsPath: (v) => (v?.trim() ? null : "Påkrevd"),
      areaPath: (v) => (v?.trim() ? null : "Påkrevd"),
      mmiPath: (v) => (v?.trim() ? null : "Påkrevd"),
      cadCompCodePath: (v) => (v?.trim() ? null : "Påkrevd"),
    },
  });

  const handleSubmit = (values: FormValues) => {
    const projectNoFromState = project?.projectNo;
    const projectNoCandidate = projectNoFromState ?? values.projectNo;
    const projectNo =
      typeof projectNoCandidate === "string" ? Number(projectNoCandidate) : projectNoCandidate;

    const payload = {
      project: {
        projectNo,
        title: values.title.trim(),
        compCodePath: values.compCodePath.trim(),
        pnsPath: values.pnsPath.trim(),
        areaPath: values.areaPath.trim(),
        mmiPath: values.mmiPath.trim(),
        cadCompCodePath: values.cadCompCodePath.trim(),
        disciplineFileAttr: values.disciplineFileAttr ?? undefined,
      },
    } as const;

    editProject({ body: payload });
  };

  return (
    <Card withBorder radius="lg" p="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Text size="sm" c="dimmed">
            Oppdater metadata for prosjektet. <Code>projectNo</Code> hentes fra URL-en og sendes
            automatisk.
          </Text>

          {isError && (
            <Alert color="red" title="Kunne ikke lagre" variant="light">
              {(error as any)?.data?.message || "Det oppstod en feil under lagring."}
            </Alert>
          )}

          {isSuccess && <Alert color="green">Lagret!</Alert>}

          <TextInput label="Tittel" placeholder="Rikshospitalet" {...form.getInputProps("title")} />

          <TextInput
            label="Søkesti til Artikkelnr"
            placeholder="psets.NONS_..."
            {...form.getInputProps("compCodePath")}
          />

          <TextInput
            label="Søkesti til pns"
            placeholder="psets.NRH_..."
            {...form.getInputProps("pnsPath")}
          />

          <TextInput
            label="Søkesti til area"
            placeholder="psets.Qto_Space..."
            {...form.getInputProps("areaPath")}
          />

          <TextInput
            label="Søkesti til MMI"
            placeholder="psets.NONS_..."
            {...form.getInputProps("mmiPath")}
          />

          <TextInput
            label="Søkesti til Revit Artikkelnr"
            placeholder="psets.NONS_..."
            {...form.getInputProps("cadCompCodePath")}
          />

          <Select
            label="Disciplin i fileAttr"
            placeholder="Velg hvilken fileAttr som inneholder disciplin"
            data={[
              { value: "fileAttr1", label: "fileAttr1" },
              { value: "fileAttr2", label: "fileAttr2" },
              { value: "fileAttr3", label: "fileAttr3" },
              { value: "fileAttr4", label: "fileAttr4" },
            ]}
            value={form.values.disciplineFileAttr}
            onChange={(v) =>
              form.setFieldValue("disciplineFileAttr", (v ?? "fileAttr1") as IFileAttrField)
            }
            allowDeselect={false}
          />

          <Group justify="flex-end">
            <Button type="submit" loading={isLoading}>
              Lagre
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
};

export default ProjectBasicsCard;
